import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LeadResourceCallForm from '../LeadResourceCallForm';
import styles from './LeadWorkModal.module.scss';

const LeadWorkModal = ({
  task,
  isClaimedByYou,
  onClose,
  onLeadUpdated,
  onSendConsent,
  isSendingConsent,
  onDisqualify,
  isDisqualifying,
}) => {
  const formRef = useRef(null);
  const [formState, setFormState] = useState({ canSave: false, isSaving: false });
  const handleFormState = useCallback((next) => setFormState(next), []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!task) return null;

  const consentAlreadySent = !!task.metadata?.consent_dispatch;
  const isBusy = formState.isSaving || isSendingConsent || isDisqualifying;
  const leadName = task.patient?.name || 'Lead';

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-work-modal-title"
      >
        <div className={styles.modalHeader}>
          <div>
            <div id="lead-work-modal-title" className={styles.modalTitle}>
              {leadName}
            </div>
            <div className={styles.modalSubtitle}>
              {task.title || task.subject}
              {isClaimedByYou ? ' · claimed by you' : ''}
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <LeadResourceCallForm
            ref={formRef}
            leadResourceId={task.leadResourceId}
            onLeadUpdated={onLeadUpdated}
            onActionStateChange={handleFormState}
            title="Lead details"
            hideActions
          />
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.modalSecondaryAction} ${styles.modalDangerAction}`}
            disabled={isBusy || !isClaimedByYou || consentAlreadySent}
            title={
              consentAlreadySent
                ? 'The consent form has been sent; the lead can no longer be disqualified here'
                : ''
            }
            onClick={() => onDisqualify?.(task)}
          >
            {isDisqualifying ? 'Disqualifying...' : 'Disqualify lead'}
          </button>
          <span className={styles.footerSpacer} />
          <button
            type="button"
            className={styles.modalSecondaryAction}
            disabled={isBusy || !isClaimedByYou}
            title={isClaimedByYou ? '' : 'Claim this task before sending the consent form'}
            onClick={() => onSendConsent?.(task)}
          >
            {isSendingConsent
              ? 'Sending...'
              : consentAlreadySent
                ? 'Resend consent form'
                : 'Send consent form'}
          </button>
          <button
            type="button"
            className={styles.modalPrimaryAction}
            disabled={isBusy || !formState.canSave}
            onClick={() => formRef.current?.save()}
          >
            {formState.isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

LeadWorkModal.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string,
    leadResourceId: PropTypes.string,
    title: PropTypes.string,
    subject: PropTypes.string,
    metadata: PropTypes.object,
    patient: PropTypes.object,
  }),
  isClaimedByYou: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onLeadUpdated: PropTypes.func,
  onSendConsent: PropTypes.func,
  isSendingConsent: PropTypes.bool,
  onDisqualify: PropTypes.func,
  isDisqualifying: PropTypes.bool,
};

export default LeadWorkModal;
